import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import blogsBg from '../assets/Resort/79.jpeg';
import blogImage1 from '../assets/Ayurveda/IMG_1687.jpeg';
import blogImage2 from '../assets/Ayurveda/65.jpeg';
import blogImage3 from '../assets/Resort/88.jpeg';
import blogImage4 from '../assets/Ayurveda/67.jpeg';
import blogImage5 from '../assets/Resort/130.jpeg';
import blogImage6 from '../assets/Resort/101.jpeg';

// Animation variants (unchanged)
const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: 'easeOut' } },
};

const Blogs = () => {
    const { t } = useTranslation('blogs');
    const [selectedPost, setSelectedPost] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const openModal = (post) => {
        setSelectedPost(post);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedPost(null);
    };

    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape' && isModalOpen) {
                closeModal();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isModalOpen]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogPosts = [
        {
            key: 'ayurveda',
            title: t('ayurvedaTitle'),
            excerpt: t('ayurvedaExcerpt'),
            description: t('ayurvedaDescription'),
            image: blogImage1,
            modalImages: [blogImage1],
            altTexts: [t('ayurvedaAlt')]
        },
        {
            key: 'healing',
            title: t('healingTitle'),
            excerpt: t('healingExcerpt'),
            description: t('healingDescription'),
            image: blogImage2,
            modalImages: [blogImage2],
            altTexts: [t('healingAlt')]
        },
        {
            key: 'nature',
            title: t('natureTitle'),
            excerpt: t('natureExcerpt'),
            description: t('natureDescription'),
            image: blogImage3,
            modalImages: [blogImage3],
            altTexts: [t('natureAlt')]
        },
        {
            key: 'food',
            title: t('foodTitle'),
            excerpt: t('foodExcerpt'),
            description: t('foodDescription'),
            image: blogImage4,
            modalImages: [blogImage4],
            altTexts: [t('foodAlt')]
        },
        {
            key: 'yoga',
            title: t('yogaTitle'),
            excerpt: t('yogaExcerpt'),
            description: t('yogaDescription'),
            image: blogImage5,
            modalImages: [blogImage5],
            altTexts: [t('yogaAlt')]
        },
        {
            key: 'lifestyle',
            title: t('lifestyleTitle'),
            excerpt: t('lifestyleExcerpt'),
            description: t('lifestyleDescription'),
            image: blogImage6,
            modalImages: [blogImage6],
            altTexts: [t('lifestyleAlt')]
        },
    ];

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Hero Section */}
            <div className="relative flex h-[60vh] w-full items-center justify-center font-lato sm:h-[70vh] md:h-[50vh] lg:h-[80vh]">
                <img
                    src={blogsBg}
                    alt={t('heroAlt')}
                    className="absolute inset-0 h-full w-full object-cover"
                />

                {/* Background overlay - stronger on mobile */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 sm:from-black/60 sm:via-black/40 sm:to-black/60 md:from-black/50 md:via-black/30 md:to-black/50" />

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={textVariants}
                    className="container relative z-10 flex items-center justify-center  md:pt-[100px]"
                >
                    <div className="
            w-full max-w-4xl 
            
            
            px-6 sm:px-10 
            rounded-2xl sm:rounded-3xl 
            
        ">
                        <h1 className="
                text-center text-[42px] sm:text-[52px] md:text-[60px] lg:text-[80px] 
                font-semibold leading-tight text-white font-copperplate
            ">
                            {t('heroTitle')}
                        </h1>

                        <div className="flex justify-center text-center mt-6 sm:mt-8">
                            <p className="
                    max-w-[95%] sm:max-w-2xl 
                    font-lato text-[17px] sm:text-[20px] md:text-[25px] 
                    font-semibold tracking-wide text-white leading-relaxed
                ">
                                {t('heroSubtitle1')}{' '}
                                <span className="text-[#BF943A] font-semibold">
                                    {t('heroSubtitle2')}
                                </span>
                                {t('heroSubtitle3')}
                            </p>
                        </div>
                    </div>

                    {/* decorative svgs can go here if needed */}
                </motion.div>
            </div>

            {/* Blog Posts Section */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container pt-[40px] font-lato text-white md:pt-[80px]">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="flex items-center gap-5"
                    >
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[25px] font-semibold uppercase text-white md:text-[30px] lg:text-[40px]">
                            {t('sectionTitle')}
                        </h1>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="mt-2 flex justify-between"
                    >
                        <div className="w-full text-[12px] font-semibold text-[#AEAEAE] md:text-[16px] lg:w-[60%] lg:text-[18px]">
                            {t('sectionDescription')}
                        </div>
                    </motion.div>

                    <div className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></div>

                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                        {blogPosts.map((post, index) => (
                            <motion.div
                                key={index}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={scaleUp}
                                className="bg-[#1a1a1a] rounded-lg overflow-hidden cursor-pointer"
                                onClick={() => openModal(post)}
                            >
                                <img
                                    src={post.image}
                                    alt={post.title}
                                    className="h-[200px] w-full object-cover md:h-[250px]"
                                />
                                <div className="p-6">
                                    <h2 className="text-[20px] font-semibold text-white md:text-[22px]">
                                        {post.title}
                                    </h2>
                                    <p className="mt-2 text-[14px] text-[#C2C2C2] font-lato md:text-[16px]">
                                        {post.excerpt}
                                    </p>
                                    <button
                                        className="mt-4 px-4 py-2 bg-[#BF943A] rounded-lg font-lato font-semibold text-[14px] text-[#0C0C0C] transition-all duration-300 hover:bg-[#A07A2F] md:text-[16px]"
                                    >
                                        {t('readMore')}
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Modal Window */}
            {isModalOpen && selectedPost && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
                    onClick={closeModal}
                    aria-modal="true"
                    role="dialog"
                    aria-labelledby="modal-title"
                >
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={modalVariants}
                        className="relative bg-[#1a1a1a] rounded-lg border border-gray-600 shadow-2xl w-full max-w-[95vw] sm:max-w-3xl md:max-w-5xl mx-2 max-h-[90vh] overflow-y-auto p-4 sm:p-6 md:p-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 text-white text-[28px] sm:text-[32px] hover:bg-[#BF943A]/20 hover:text-[#BF943A] rounded-full w-10 h-10 flex items-center justify-center transition"
                            aria-label="Close modal"
                        >
                            ×
                        </button>

                        <h2 id="modal-title" className="text-[20px] sm:text-[24px] md:text-[28px] font-semibold text-white">
                            {selectedPost.title}
                        </h2>

                        <p className="mt-4 text-[14px] sm:text-[16px] md:text-[18px] text-[#C2C2C2] font-lato leading-relaxed">
                            {selectedPost.description}
                        </p>

                        <div className="mt-6 flex flex-col sm:gap-6 gap-4 md:flex-row md:gap-6">
                            {selectedPost.modalImages.map((image, idx) => (
                                <img
                                    key={idx}
                                    src={image}
                                    alt={selectedPost.altTexts[idx] || selectedPost.title}
                                    className="h-[120px] sm:h-[150px] md:h-[200px] w-full md:w-1/3 object-cover rounded-lg"
                                />
                            ))}
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default Blogs;